import { inject, Injector, Signal } from '@angular/core';
import { HttpRequest, HttpEvent, HttpHeaders, HttpHandlerFn } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RETAIL_BRAND_NAME } from '@lentau/ui-kit/core/tokens/brand';
import { BrowserApiSupportService } from '@lentau/ui-kit/core/services/browser-api-support/browser-api-support.service';
import { ApiDataService } from '@lentau/core/api/application';
import { DeliveryModeFacade } from '@lentau/delivery-mode/store/delivery-mode.facade';
import { CityStoreFacade } from '@lentau/city/store/city.facade';
import { MARKETING_PARTNER_KEY_INJECTION_TOKEN } from '@lentau/mpk/shared/marketing-partner-key.token';
import { RequestHeaderParams } from '../shared/enums';
import { ApiGatewayUrls, ApiGatewayUrlsPostWithFile, deliveryModeExceptionUrls } from '../shared/consts';
import { getAppVersion } from '@lentau/api/shared/shared/api.helper';
import { getRequestFromFormUrlencoded } from '../shared/helper';
import { RequestData } from '@lentau/api/shared/monolith/types';
import { JsonRpcBody } from '@lentau/api/shared/jrpc/types';
import { isJsonString } from '@lentau/ui-kit/core/utils/object.utils';
import { RetailBrands } from '@lentau/ui-kit/core/tokens/constants';
import { DeviceIdService } from '@lentau/core/api/shared/device-id.service';
import { WebPlatformService } from '@lentau/analytics/events/shared/services/web-platform-detector.service';
import { WebPlatform } from '@lentau/ui-kit/core/enums/web-platform.enum';
import { LOCATION } from '@lentau/ui-kit/core/tokens/location';
import { getQueryHost } from '@lentau/seo/shared/get-query-host.helper';
import { getRetailBrandHeader } from '@lentau/seo/shared/get-retail-brand-header.helper';
import { ADID, AdidResolver } from '@lentau/ui-kit/core/tokens/adid.token';
import { ADVERTISING_ID, AdvertisingIdResolver } from '@lentau/ui-kit/core/tokens/advertising-id.token';
import { DEVICE_OS, DeviceOsResolver } from '@lentau/ui-kit/core/tokens/device-os.token';
import { STOREFRONT_LABELS, StorefrontLabelsResolver } from '@lentau/ui-kit/core/tokens/storefront-labels.token';
import { environment } from '@lentau/core/environments';
import { ApiGatewayParams, ApiRestParams, JrpcParams, StorefrontHeadersParams } from './data.interceptor.types';
import {
  OKEY_APP_STOREFRONT_LABEL,
  TELEGRAM_MINI_APP_QUERY_PARAM,
  TELEGRAM_MINI_APP_QUERY_VALUE,
} from './data.interceptor.constants';

export function dataInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const injector: Injector = inject(Injector);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return handleDataInterceptor(req as HttpRequest<any>, next, injector);
}

export function handleDataInterceptor(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  injector: Injector
): Observable<HttpEvent<unknown>> {
  const retailBrand: RetailBrands = injector.get(RETAIL_BRAND_NAME);
  const apiDataService: ApiDataService = injector.get(ApiDataService);
  const deviceIdService: DeviceIdService = injector.get(DeviceIdService);
  const browserApi: BrowserApiSupportService = injector.get(BrowserApiSupportService);
  const deliveryModeFacade: DeliveryModeFacade = injector.get(DeliveryModeFacade);
  const cityFacade: CityStoreFacade = injector.get(CityStoreFacade);
  const webPlatformService: WebPlatformService = injector.get(WebPlatformService);
  const marketingPartnerKey: Signal<string> = injector.get(MARKETING_PARTNER_KEY_INJECTION_TOKEN);
  const location: Location = injector.get(LOCATION);
  const getAdid: AdidResolver = injector.get(ADID);
  const getAdvertisingId: AdvertisingIdResolver = injector.get(ADVERTISING_ID);
  const getDeviceOs: DeviceOsResolver = injector.get(DEVICE_OS);
  const getStorefrontLabels: StorefrontLabelsResolver = injector.get(STOREFRONT_LABELS);

  const webPlatform: WebPlatform = webPlatformService.detect();
  const deviceId: string = deviceIdService.getDeviceId();
  const sessionToken: string = apiDataService.getSessionToken();
  const userSessionId: string = apiDataService.getUserSessionId();
  const xOrganizationId: string = apiDataService.getXOrganizationId();
  const xPassportId: string = apiDataService.getXPassportId();
  const requestId: (method: string) => string = (method: string): string => apiDataService.getRequestId(method);
  const cityDomain: string = cityFacade.citySlug();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let reqCopy: HttpRequest<any> = getRequestWithCommonData(request, retailBrand);

  if (browserApi.isBrowser()) {
    if (
      request.body instanceof FormData &&
      !ApiGatewayUrlsPostWithFile.some((path: string) => request.url.includes(path))
    ) {
      reqCopy = getRequestFormData(reqCopy, apiDataService, deviceIdService, cityDomain);
      return next(reqCopy);
    }
  }

  const adid: string | null = getAdid();
  const advertisingId: string | null = getAdvertisingId();
  const deviceOs: string | null = getDeviceOs();
  const storefrontLabels: string | null = getStorefrontLabels();

  if (request.url.includes('jrpc')) {
    reqCopy = getRequestJRPC({
      adid,
      advertisingId,
      cityDomain,
      deviceId,
      deviceOs,
      request: reqCopy,
      requestId,
      sessionToken,
      storefrontLabels,
      userSessionId,
      webPlatform,
      xOrganizationId,
      xPassportId,
    });
  } else if (request.url.includes('api/rest')) {
    reqCopy = getRequestApiRest({
      adid,
      advertisingId,
      cityDomain,
      deviceId,
      deviceOs,
      request: reqCopy,
      requestId,
      sessionToken,
      storefrontLabels,
      userSessionId,
      webPlatform,
      xOrganizationId,
      xPassportId,
    });
  } else if (ApiGatewayUrls.some((path: string) => request.url.includes(path))) {
    reqCopy = getHeaderForApiGatewayRequest({
      adid,
      advertisingId,
      cityDomain,
      deliveryMode: deliveryModeFacade.type(),
      deviceId,
      deviceOs,
      request: reqCopy,
      sessionToken,
      storefrontLabels,
      userSessionId,
      webPlatform,
      xOrganizationId,
    });
  }

  if (request.url.includes('/delivery/interval/nearest/')) {
    reqCopy = addMarketingPartnerKey(reqCopy, marketingPartnerKey());
  }

  reqCopy = addTelegramMiniAppHeader(reqCopy, browserApi, location);
  reqCopy = addOkeyAppStorefrontLabelHeader(reqCopy, browserApi, webPlatform);

  return next(reqCopy);
}

function getRequestFormData(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  request: HttpRequest<any>,
  apiDataService: ApiDataService,
  deviceIdService: DeviceIdService,
  cityDomain: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): HttpRequest<any> {
  const requestBodyFormData: FormData = request.body as FormData;
  const requestBodyString: string = request.body.get('request') as string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requestBody: any = JSON.parse(requestBodyString);

  requestBodyFormData.delete('request');

  requestBody.Head = {
    ...requestBody?.Head,
    DeviceId: deviceIdService.getDeviceId(),
    Domain: cityDomain,
    SessionToken: apiDataService.getSessionToken(),
  };

  requestBodyFormData.append('request', JSON.stringify(requestBody));

  return request.clone({
    headers: request.headers.set(RequestHeaderParams.X_USER_SESSION_ID, apiDataService.getUserSessionId()),
    body: requestBodyFormData,
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getRequestWithCommonData(request: HttpRequest<any>, retailBrand: RetailBrands): HttpRequest<any> {
  return request.clone({
    headers: request.headers
      .set(RequestHeaderParams.X_RETAIL_BRAND, getRetailBrandHeader(retailBrand))
      .set(RequestHeaderParams.X_QUERY_HOST, getQueryHost(retailBrand))
      .set(
        RequestHeaderParams.APP_VERSION,
        request.headers.get(RequestHeaderParams.APP_VERSION) ?? environment.APP_VERSION
      ),
  });
}

function getRequestApiRest(params: ApiRestParams): HttpRequest<unknown> {
  const {
    adid,
    advertisingId,
    cityDomain,
    deviceId,
    deviceOs,
    request,
    requestId,
    sessionToken,
    storefrontLabels,
    userSessionId,
    webPlatform,
    xOrganizationId,
    xPassportId,
  }: ApiRestParams = params;
  const requestBody: RequestData = getRequestFromFormUrlencoded(request);

  requestBody.Head = {
    ...requestBody.Head,
    RequestId: requestId(requestBody.Head.Method),
    DeviceId: deviceId,
    Domain: cityDomain,
    SessionToken: sessionToken,
  };

  const body: string = `request=${JSON.stringify(requestBody)}`;

  let headers: HttpHeaders = request.headers
    .set(RequestHeaderParams.CONTENT_TYPE, 'application/x-www-form-urlencoded')
    .set(RequestHeaderParams.X_ORGANIZATION_ID, xOrganizationId)
    .set(RequestHeaderParams.X_PASSPORT_ID, xPassportId)
    .set(RequestHeaderParams.X_USER_SESSION_ID, userSessionId);

  headers = applyStorefrontHeaders(headers, {
    adid,
    advertisingId,
    deviceOs,
    storefrontLabels,
    webPlatform,
  });

  return request.clone({
    headers,
    body,
  });
}

function getRequestJRPC(params: JrpcParams): HttpRequest<unknown> {
  const {
    adid,
    advertisingId,
    cityDomain,
    deviceId,
    deviceOs,
    request,
    requestId,
    sessionToken,
    storefrontLabels,
    userSessionId,
    webPlatform,
    xOrganizationId,
    xPassportId,
  }: JrpcParams = params;
  const requestBodyRaw: string = request.body as string;
  const jrpcHeaderParams: Omit<JrpcParams, 'request' | 'requestId'> & { headers: HttpHeaders } = {
    adid,
    advertisingId,
    cityDomain,
    deviceId,
    deviceOs,
    headers: request.headers,
    sessionToken,
    storefrontLabels,
    userSessionId,
    webPlatform,
    xOrganizationId,
    xPassportId,
  };

  if (!isJsonString(requestBodyRaw)) {
    return request.clone({
      headers: addHeadersJRPC(jrpcHeaderParams),
      body: request.body,
    });
  }
  let requestBody: JsonRpcBody = JSON.parse(requestBodyRaw);

  requestBody = {
    ...requestBody,
    id: requestId(requestBody.method),
  };

  return request.clone({
    headers: addHeadersJRPC(jrpcHeaderParams),
    body: JSON.stringify(requestBody),
  });
}

function addHeadersJRPC(params: Omit<JrpcParams, 'request' | 'requestId'> & { headers: HttpHeaders }): HttpHeaders {
  const {
    adid,
    advertisingId,
    cityDomain,
    deviceId,
    deviceOs,
    headers,
    sessionToken,
    storefrontLabels,
    userSessionId,
    webPlatform,
    xOrganizationId,
    xPassportId,
  }: Omit<JrpcParams, 'request' | 'requestId'> & { headers: HttpHeaders } = params;

  const result: HttpHeaders = headers
    .set(RequestHeaderParams.DEVICE_ID, deviceId)
    .set(RequestHeaderParams.SESSION_TOKEN, sessionToken)
    .set(RequestHeaderParams.CLIENT, getAppVersion())
    .set(RequestHeaderParams.X_DOMAIN, cityDomain)
    .set(RequestHeaderParams.X_ORGANIZATION_ID, xOrganizationId)
    .set(RequestHeaderParams.X_PASSPORT_ID, xPassportId)
    .set(RequestHeaderParams.X_USER_SESSION_ID, userSessionId);

  return applyStorefrontHeaders(result, {
    adid,
    advertisingId,
    deviceOs,
    storefrontLabels,
    webPlatform,
  });
}

function getHeaderForApiGatewayRequest(params: ApiGatewayParams): HttpRequest<unknown> {
  return params.request.clone({
    headers: setHeadersApiGateway(params),
  });
}

function applyStorefrontHeaders(headers: HttpHeaders, params: StorefrontHeadersParams): HttpHeaders {
  const { adid, advertisingId, deviceOs, storefrontLabels, webPlatform }: StorefrontHeadersParams = params;
  let result: HttpHeaders = headers;

  if (!storefrontLabels) {
    result = result.set(RequestHeaderParams.X_DEVICE_WEB_PLATFORM, webPlatform);
  }

  if (deviceOs) {
    result = result.set(RequestHeaderParams.X_DEVICE_OS, deviceOs);
  }

  if (storefrontLabels) {
    result = result.set(RequestHeaderParams.X_STOREFRONT_LABELS, storefrontLabels);
  }

  if (adid) {
    result = result.set(RequestHeaderParams.ADID, adid);
  }

  if (advertisingId) {
    result = result.set(RequestHeaderParams.ADVERTISING_ID, advertisingId);
  }

  return result;
}

function setHeadersApiGateway(params: ApiGatewayParams): HttpHeaders {
  const {
    adid,
    advertisingId,
    cityDomain,
    deliveryMode,
    deviceId,
    deviceOs,
    request,
    sessionToken,
    storefrontLabels,
    userSessionId,
    webPlatform,
    xOrganizationId,
  }: ApiGatewayParams = params;

  let headers: HttpHeaders = request.headers
    .set(RequestHeaderParams.DEVICE_ID, deviceId)
    .set(RequestHeaderParams.SESSION_TOKEN, sessionToken)
    .set(RequestHeaderParams.X_DOMAIN, cityDomain)
    .set(RequestHeaderParams.X_DEVICE_ID, deviceId)
    .set(RequestHeaderParams.X_ORGANIZATION_ID, xOrganizationId)
    .set(RequestHeaderParams.CLIENT, getAppVersion())
    .set(RequestHeaderParams.X_USER_SESSION_ID, userSessionId);

  headers = applyStorefrontHeaders(headers, {
    adid,
    advertisingId,
    deviceOs,
    storefrontLabels,
    webPlatform,
  });

  if (!deliveryModeExceptionUrls.find((exceptionUrl: string) => request.url.includes(exceptionUrl))) {
    headers = headers.set(RequestHeaderParams.X_DELIVERY_MODE, deliveryMode);
  }

  return headers;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function addMarketingPartnerKey(request: HttpRequest<any>, key: string): HttpRequest<any> {
  return request.clone({
    headers: request.headers.set(RequestHeaderParams.ADD_MARKETING_PARTNER_KEY, key),
  });
}

function addTelegramMiniAppHeader(
  request: HttpRequest<unknown>,
  browserApi: BrowserApiSupportService,
  location: Location
): HttpRequest<unknown> {
  if (!browserApi.isBrowser()) {
    return request;
  }

  const search: string = location.search ?? '';
  const queryParams: URLSearchParams = new URLSearchParams(search);
  const lentaTgKey: string | null = queryParams.get(TELEGRAM_MINI_APP_QUERY_PARAM);

  if (lentaTgKey !== TELEGRAM_MINI_APP_QUERY_VALUE) {
    return request;
  }

  return request.clone({
    headers: request.headers.set(RequestHeaderParams.X_LENTA_TG_KEY, TELEGRAM_MINI_APP_QUERY_VALUE),
  });
}

function addOkeyAppStorefrontLabelHeader(
  request: HttpRequest<unknown>,
  browserApi: BrowserApiSupportService,
  webPlatform: WebPlatform
): HttpRequest<unknown> {
  if (!browserApi.isBrowser() || webPlatform !== WebPlatform.OKEY_APP) {
    return request;
  }

  return request.clone({
    headers: request.headers.set(RequestHeaderParams.X_STOREFRONT_LABEL, OKEY_APP_STOREFRONT_LABEL),
  });
}
